import {
  Button,
  DateFormats,
  Dropdown,
  formatISODate,
  Inline,
  type OptionType,
} from '@omnipresentgroup/design-system';
import { useGetTimesheets } from '@omnipresentgroup/domains';
import type { TimesheetArbeitszeit } from '@omnipresentgroup/types';
import { DateTime } from 'luxon';
import { memo, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { EMPTY_ARRAY } from '../../../constants';

const today = DateTime.now().toISODate();

type Props = {
  timesheet?: TimesheetArbeitszeit;
};

export const TimesheetDetailsNavigation = memo(({ timesheet }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const employeeId = new URLSearchParams(location.search).get(
    'employeeId',
  ) as string;

  const { data: timesheets, isLoading: isLoadingTimesheets } = useGetTimesheets(
    {
      employeeId,
      enabled: !!employeeId,
    },
  );

  const navigateToToday = useCallback(() => {
    const queryParameters = new URLSearchParams(location.search);
    queryParameters.set('weekDate', today);
    navigate(`?${queryParameters.toString()}`);
  }, [location.search, navigate]);

  const navigateToPreviousWeek = useCallback(() => {
    const queryParameters = new URLSearchParams(location.search);
    const previousWeekDate = DateTime.fromISO(
      queryParameters.get('weekDate') || today,
    )
      .minus({ days: 7 })
      .toISODate();
    queryParameters.set('weekDate', previousWeekDate ?? '');
    navigate(`?${queryParameters.toString()}`);
  }, [location.search, navigate]);

  const navigateToNextWeek = useCallback(() => {
    const queryParameters = new URLSearchParams(location.search);
    const nextWeekDate = DateTime.fromISO(
      queryParameters.get('weekDate') || today,
    )
      .plus({ days: 7 })
      .toISODate();
    queryParameters.set('weekDate', nextWeekDate ?? '');
    navigate(`?${queryParameters.toString()}`);
  }, [location.search, navigate]);

  const options: OptionType[] = useMemo(
    () =>
      timesheets?.map((sheet) => ({
        label: `${formatISODate(sheet?.startDate, DateFormats.Date)}
      - ${formatISODate(sheet?.endDate, DateFormats.Date)}`,
        value: sheet.id,
      })) || EMPTY_ARRAY,
    [timesheets],
  );

  return (
    <Inline maxW={400} w="stretch" stretch={4}>
      <Button variant="secondary" size="small" onClick={navigateToToday}>
        Today
      </Button>
      <Button
        variant="secondary"
        icon="ChevronLeft"
        size="small"
        onClick={navigateToPreviousWeek}
      />
      <Button
        variant="secondary"
        icon="ChevronRight"
        size="small"
        onClick={navigateToNextWeek}
      />
      <Dropdown
        id="timesheet-dropdown"
        isMulti={false}
        size="small"
        isLoading={!!employeeId && isLoadingTimesheets}
        options={options}
        value={options.find((option) => option.value === timesheet?.id)}
        onChange={(option) => {
          if (option?.value) {
            const selectedTimesheet = timesheets?.find(
              (sheet) => sheet.id === option.value,
            );
            if (selectedTimesheet) {
              const queryParameters = new URLSearchParams(location.search);
              queryParameters.set('weekDate', selectedTimesheet.startDate);
              navigate(`?${queryParameters.toString()}`);
            }
          }
        }}
        isDisabled={!employeeId}
      />
    </Inline>
  );
});
